import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { Option } from '../types/database'

// Cache for options by IDs to prevent redundant fetches
const optionsByIdCache = new Map<string, Map<string, Option>>()

/**
 * Hook to fetch options by their option IDs directly.
 * This is useful for sold aircraft where option IDs are stored in transaction snapshots
 * rather than in the aircraft table.
 */
export function useOptionsByIds(optionIds: string[]) {
  const cacheKey = [...optionIds].sort().join(',')
  const [data, setData] = useState<Map<string, Option>>(() => {
    return optionsByIdCache.get(cacheKey) || new Map()
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dataVersion, setDataVersion] = useState(0)

  useEffect(() => {
    if (optionIds.length === 0) {
      setData(new Map())
      return
    }

    // Check if we already have cached data
    const cached = optionsByIdCache.get(cacheKey)
    if (cached && cached.size > 0) {
      setData(cached)
      setDataVersion(v => v + 1)
      return
    }

    const fetchOptions = async () => {
      setLoading(true)
      setError(null)

      try {
        // Fetch options directly by their IDs
        const { data: optionsData, error: optionsError } = await supabase
          .from('options')
          .select('*')
          .in('option_id', optionIds)

        if (optionsError) throw optionsError

        // Create a map of option_id to Option
        const optionsMap = new Map<string, Option>()
        optionsData?.forEach((option: Option) => {
          optionsMap.set(option.option_id, option)
        })

        setData(optionsMap)
        optionsByIdCache.set(cacheKey, optionsMap) // Cache the result
        setDataVersion(v => v + 1)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch options')
      } finally {
        setLoading(false)
      }
    }

    fetchOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])

  // Memoize the data to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedData = useMemo(() => data, [dataVersion])

  return { data: memoizedData, loading, error }
}

